/**
 * DoughBro design tokens.
 *
 * `palette` holds the raw brand colours — theme-invariant, use it only where a
 * colour is genuinely fixed (a logo mark, an illustration). Everything else
 * reads the semantic `ThemeColors` set via `useTheme()` so it follows
 * light / dark.
 */
export const palette = {
  crust: "#8B5E3C",
  crumb: "#F5EDD8",
  dough: "#ECD9B0",
  golden: "#D4A04A",
  flour: "#FAF6EE",
  sesame: "#C17F3A",
  rye: "#6B4226",
  poppy: "#B85C3A",
  ink: "#2C1A0E",
  mist: "#A8927A",
  parchment: "#F0E4C8",
  starter: "#5A8A5E",
  warning: "#C8791E",
  error: "#B85C3A",
  white: "#FFFFFF",
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  full: 9999,
} as const;

export const fontSizes = {
  xs: 11,
  sm: 13,
  md: 16,
  lg: 19,
  xl: 22,
  xxl: 26,
  hero: 34,
} as const;

export const fontWeights = {
  regular: "400" as const,
  medium: "500" as const,
  semibold: "600" as const,
  bold: "700" as const,
  extrabold: "800" as const,
};

/**
 * Brand typefaces — matched to the marketing site (doughbro-website).
 * Shrikhand is display only (large screen/section titles); everything else is
 * Nunito Sans. Loaded in src/app/_layout.tsx.
 */
export const fonts = {
  display: "Shrikhand_400Regular",
  heading: "Shrikhand_400Regular",
  subheading: "NunitoSans_800ExtraBold",
  body: "NunitoSans_400Regular",
  bodyMedium: "NunitoSans_600SemiBold",
  bodySemibold: "NunitoSans_700Bold",
  bodyBold: "NunitoSans_800ExtraBold",
} as const;

/** One soft elevation scale, mirroring the site's --shadow-sm / -md / -lg. */
export const shadows = {
  sm: {
    shadowColor: palette.ink,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: palette.ink,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 4,
  },
  lg: {
    shadowColor: palette.ink,
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.14,
    shadowRadius: 32,
    elevation: 10,
  },
} as const;

export const control = {
  height: 48,
  heightCompact: 36,
  heightTall: 54,
} as const;

export type ColorScheme = "light" | "dark";

/**
 * Semantic colour contract, shared by name across the app fleet. Extend per
 * product, but keep the core names identical so screens read the same
 * everywhere.
 */
export type ThemeColors = {
  background: string;
  surface: string;
  surfaceMuted: string;
  foreground: string;
  muted: string;
  heading: string;
  border: string;
  borderStrong: string;
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  accentSurface: string;
  danger: string;
  dangerSurface: string;
  success: string;
  successSurface: string;
  warning: string;
  warningSurface: string;
  focusRing: string;
  overlay: string;
};

export const lightColors: ThemeColors = {
  background: palette.crumb,
  surface: palette.white,
  surfaceMuted: palette.parchment,
  foreground: palette.ink,
  muted: palette.mist,
  heading: palette.rye,
  border: palette.parchment,
  borderStrong: "#D9C39A",
  primary: palette.crust,
  primaryForeground: palette.white,
  accent: palette.golden,
  accentForeground: palette.ink,
  accentSurface: "#FBF0DF",
  danger: palette.error,
  dangerSurface: "#F7E6DF",
  success: palette.starter,
  successSurface: "#E7F0E8",
  warning: palette.warning,
  warningSurface: "#FBF0DF",
  focusRing: palette.golden,
  overlay: "rgba(44, 26, 14, 0.45)",
};

/**
 * Dark palette — derived from the light one (3 Sep 2026, pending Brad's
 * review in the running app): deep warm-brown grounds, cream text, and the
 * golden accent promoted to primary because the crust brown reads muddy on a
 * dark surface.
 */
export const darkColors: ThemeColors = {
  background: "#1E1206",
  surface: "#2A1A0E",
  surfaceMuted: "#35271A",
  foreground: "#F3E7CE",
  muted: "#A78C6E",
  heading: "#E7C79A",
  border: "#42301F",
  borderStrong: "#5A4028",
  primary: "#D6A05A",
  primaryForeground: "#241505",
  accent: "#E4B063",
  accentForeground: "#241505",
  accentSurface: "#3A2B12",
  danger: "#E08763",
  dangerSurface: "#3A211A",
  success: "#8FBF93",
  successSurface: "#1F2E20",
  warning: "#E3A45A",
  warningSurface: "#3A2B12",
  focusRing: "#E4B063",
  overlay: "rgba(0, 0, 0, 0.6)",
};

export const tokens = {
  palette,
  spacing,
  radius,
  fontSizes,
  fontWeights,
  fonts,
  shadows,
  control,
  lightColors,
  darkColors,
} as const;
